import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { API_BASE } from "@/lib/api";
import { Plus, FileText, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import InvoiceCard from "@/components/portal/invoices/PortalInvoiceCard";
import { toast } from "sonner";


const InvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const clientIdFilter = searchParams.get("client_id") || "";
  const [clientName, setClientName] = useState("");

  useEffect(() => {
    fetchInvoices();
    fetchStats();
  }, [statusFilter, clientIdFilter]);

  useEffect(() => {
    if (!clientIdFilter) { setClientName(""); return; }
    let cancelled = false;
    api.get("/portal/invoices/clients").then((r) => {
      if (cancelled) return;
      const match = (r.data || []).find((c) => c.id === clientIdFilter);
      setClientName(match ? match.name : "this client");
    }).catch(() => { if (!cancelled) setClientName("this client"); });
    return () => { cancelled = true; };
  }, [clientIdFilter]);

  const fetchInvoices = async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (clientIdFilter) params.client_id = clientIdFilter;
      const response = await api.get("/portal/invoices", { params });
      setInvoices(response.data);
    } catch (error) {
      toast.error("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  const clearClientFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("client_id");
    setSearchParams(next);
  };

  const fetchStats = async () => {
    try {
      const response = await api.get("/portal/invoices/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const filteredInvoices = invoices.filter((invoice) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      invoice.invoice_number.toLowerCase().includes(query) ||
      invoice.client_name.toLowerCase().includes(query) ||
      invoice.client_email.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-8" data-testid="invoice-list-page">
      {/* Page header — restored to match the original Invoicing look. The
          "Create Invoice" button is intentionally absent here because
          the shell already shows a sticky "New Invoice" CTA at the top. */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-5xl font-bold tracking-tight text-slate-900" data-testid="page-title">
            Invoices
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage and track your invoices
          </p>
        </div>
      </div>

      {/* Client-scoped filter chip — appears when navigated in from
          the Clients page via ?client_id. */}
      {clientIdFilter && (
        <div
          className="flex items-center gap-2 rounded-full bg-slate-100 border border-slate-300 pl-4 pr-2 py-1.5 text-sm w-fit"
          data-testid="client-filter-chip"
        >
          <span className="font-medium text-slate-700">
            Viewing invoices for <span className="font-bold">{clientName || "this client"}</span>
          </span>
          <button
            type="button"
            onClick={clearClientFilter}
            className="p-1 rounded-full hover:bg-slate-200"
            data-testid="clear-client-filter"
            aria-label="Clear client filter"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="stats-section">
          <Card 
            className={`p-6 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "all" ? "ring-2 ring-[#dedd0a]" : ""}`}
            onClick={() => setStatusFilter("all")}
            data-testid="stat-card-total"
          >
            <p className="text-sm font-medium text-muted-foreground">Total Invoices</p>
            <p className="text-4xl font-bold mt-3 text-slate-900" data-testid="stat-total">
              {stats.total_invoices}
            </p>
          </Card>
          <Card 
            className={`p-6 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "draft" ? "ring-2 ring-slate-500" : ""}`}
            onClick={() => setStatusFilter("draft")}
            data-testid="stat-card-draft"
          >
            <p className="text-sm font-medium text-muted-foreground">Draft</p>
            <p className="text-4xl font-bold mt-3 text-slate-700" data-testid="stat-draft">
              {stats.draft_count}
            </p>
          </Card>
          <Card 
            className={`p-6 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "sent" ? "ring-2 ring-[#dedd0a]" : ""}`}
            onClick={() => setStatusFilter("sent")}
            data-testid="stat-card-sent"
          >
            <p className="text-sm font-medium text-muted-foreground">Sent</p>
            <p className="text-4xl font-bold mt-3 text-stone-950" data-testid="stat-sent">
              {stats.sent_count}
            </p>
          </Card>
          {/* Outstanding — DERIVED. Counts sent invoices whose due date
              has passed. Franchisees don't reconcile so amount owed
              here is always the full invoice total. */}
          <Card
            className={`p-6 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "outstanding" ? "ring-2 ring-rose-500" : ""}`}
            onClick={() => setStatusFilter("outstanding")}
            data-testid="stat-card-outstanding"
          >
            <p className="text-sm font-medium text-muted-foreground">Outstanding</p>
            <p className="text-4xl font-bold mt-3 text-rose-600" data-testid="stat-outstanding">
              {stats.outstanding_count || 0}
            </p>
            {typeof stats.outstanding_owed_total === "number" && stats.outstanding_owed_total > 0 && (
              <p className="text-xs font-semibold text-rose-600 mt-1" data-testid="stat-outstanding-owed">
                £{stats.outstanding_owed_total.toLocaleString('en-GB', { minimumFractionDigits: 2 })} owed
              </p>
            )}
          </Card>
          <Card 
            className={`p-6 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "paid" ? "ring-2 ring-emerald-500" : ""}`}
            onClick={() => setStatusFilter("paid")}
            data-testid="stat-card-paid"
          >
            <p className="text-sm font-medium text-muted-foreground">Paid</p>
            <p className="text-4xl font-bold mt-3 text-emerald-600" data-testid="stat-paid">
              {stats.paid_count}
            </p>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search invoices..."
            className="pl-10 h-12"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] h-12" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="outstanding">Outstanding</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Invoice List */}
      <div className="space-y-4" data-testid="invoices-container">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading invoices...</p>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground/50" />
            <h3 className="text-lg font-semibold mt-4">No invoices found</h3>
            <p className="text-muted-foreground mt-1">
              {searchQuery || statusFilter !== "all"
                ? "Try adjusting your filters"
                : "Create your first invoice to get started"}
            </p>
            {!searchQuery && statusFilter === "all" && (
              <Link to="/portal/invoices/new">
                <Button className="mt-6 gap-2 bg-[#dedd0a] hover:brightness-95 text-stone-950" data-testid="empty-create-btn">
                  <Plus className="w-4 h-4" />
                  Create Invoice
                </Button>
              </Link>
            )}
          </Card>
        ) : (
          filteredInvoices.map((invoice, index) => (
            <div
              key={invoice.id}
              className="animate-fade-in"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <InvoiceCard
                invoice={invoice}
                onDeleted={(id) => {
                  setInvoices((arr) => arr.filter((x) => x.id !== id));
                  fetchStats();
                }}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default InvoiceList;
